import { useRef } from "react";
import cx from "classnames";
import { faPrescription } from "@fortawesome/pro-regular-svg-icons";

import { toDollars } from "common/utils/formatting";
import LinkButton from "common/components/LinkButton";
import CatalogImage from "common/components/CatalogImage";
import Icon from "common/components/Icon";

import TextChip from "client/common/components/content/TextChip";
import { urls } from "client/routes";

import { ProductSearchResultV2 } from "../types";
import { useProductSearchCardFields } from "../hooks/utils";
import ProductSearchAutoshipPricing from "./ProductSearchAutoshipPricing";
import ProductSearchDiscountBadges from "./ProductSearchDiscountBadges";

import styles from "../styles/ProductSearchMobileResultV2.module.less";

type ProductSearchMobileResultV2Props = { result: ProductSearchResultV2 };

const ProductSearchMobileResultV2 = ({ result }: ProductSearchMobileResultV2Props) => {
  const resultRef = useRef<HTMLDivElement>(null);
  const {
    productLine,
    hasPrescriptionLabel,
    isFeatured,
    variantLabel,
    packLabel,
    optionsLabel,
    facts,
    handleClick,
  } = useProductSearchCardFields(result);

  return (
    <LinkButton
      dest={urls.products.detail({
        params: { id: productLine.id },
        search: { item: result.default_item.id, product: result.id },
      })}
      className={styles.result}
      onClick={handleClick}
    >
      {(hasPrescriptionLabel || isFeatured) && (
        <div
          className={cx(
            styles.background,
            hasPrescriptionLabel ? styles.prescribed : styles.featured,
          )}
        />
      )}
      <div className={styles.media}>
        <CatalogImage
          image={result.main_image}
          size="thumbnail"
          alt={result.name}
          className={styles.image}
        />
        <ProductSearchDiscountBadges discounts={result.default_item.discounts} styles={styles} />
      </div>
      <div className={styles.item} ref={resultRef}>
        {(hasPrescriptionLabel || isFeatured) &&
          (hasPrescriptionLabel ? (
            <TextChip size="s" variant="info">
              {result.patient_prescription_label}
            </TextChip>
          ) : (
            <TextChip size="s" variant="highlight">
              Your Vet&apos;s Choice
            </TextChip>
          ))}
        {productLine.prescription_required && (
          <Icon icon={faPrescription} className={styles.prescription} />
        )}
        <p className={styles.name}>{productLine.name}</p>
        {variantLabel && (
          <div className={styles.variant}>
            <span>{variantLabel}</span>
          </div>
        )}
        {productLine.client_description && (
          <p className={styles.desc}>{productLine.client_description}</p>
        )}
        {(packLabel || optionsLabel) && (
          <div className={styles.breakdown}>
            {packLabel && <span className={styles.pack}>{packLabel}</span>}
            {optionsLabel && <span className={styles.options}>+{optionsLabel}</span>}
          </div>
        )}
        <div className={styles.meta}>
          {result.default_item.standard_list_price > result.default_item.standard_price && (
            <span className={styles.priceWas}>
              {toDollars(result.default_item.standard_list_price)}
            </span>
          )}
          <span className={styles.price}>{toDollars(result.default_item.standard_price)}</span>
          <ProductSearchAutoshipPricing defaultItem={result.default_item} styles={styles} />
        </div>
        {/* Fact chips (compact — fewer than desktop). */}
        {facts.length > 0 && (
          <div className={styles.facts}>
            {facts.map((fact) => (
              <TextChip key={`${fact.label}-${fact.value}`} variant="grey" size="s">
                {fact.value}
              </TextChip>
            ))}
          </div>
        )}
      </div>
    </LinkButton>
  );
};

export default ProductSearchMobileResultV2;
